import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import type { AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { useLegacyDashboard } from '../../hooks/useLegacyDashboard';
import { useLegacyDashboardFilterOptions } from '../../hooks/useLegacyDashboardFilterOptions';
import { DEFAULT_LEGACY_DASHBOARD_FILTERS } from '../../models/Filters';
import {
  applyLegacyDashboardFilters,
  countActiveLegacyDashboardFilters,
} from '../../utils/applyLegacyDashboardFilters';
import type { LegacyDashboardClient } from '../../models/LegacyDashboardClient';
import { sortLegacyDashboardClients } from '../../utils/sortLegacyDashboardClients';
import { SgaProductsModal } from '../display/SgaProductsModal';
import { LegacyDashboardTable } from '../display/Table';
import { ErrorState, LoadingState } from '../feedback';
import { SummaryCards } from '../interaction/FiltersBar';
import { FiltersSection } from '../interaction/FiltersSection';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

export const Main = () => {
  const { t } = useTranslation('legacyDashboardModule');
  const { data, isLoading, isFetching, error, refetch } = useLegacyDashboard();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(DEFAULT_LEGACY_DASHBOARD_FILTERS);
  const [sorting, setSorting] = useState<SortingConfig | null>({
    field: 'orders_count',
    direction: 'desc',
  });
  const [sgaModalClient, setSgaModalClient] = useState<LegacyDashboardClient | null>(null);
  const [sgaModalOpen, setSgaModalOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const allClients = data ?? [];
  const filterOptions = useLegacyDashboardFilterOptions(allClients);

  const filteredClients = useMemo(
    () => applyLegacyDashboardFilters(allClients, filters, searchTerm),
    [allClients, filters, searchTerm],
  );

  const displayedClients = useMemo(
    () => sortLegacyDashboardClients(filteredClients, sorting),
    [filteredClients, sorting],
  );

  const summary = useMemo(() => {
    const migratedCount = allClients.filter((c) => c.is_migrated).length;
    const totalOrders = allClients.reduce((sum, c) => sum + c.avg_orders_per_month, 0);
    return {
      total: allClients.length,
      migratedCount,
      notMigratedCount: allClients.length - migratedCount,
      avgOrdersPerMonth: allClients.length > 0 ? totalOrders / allClients.length : 0,
    };
  }, [allClients]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleViewSgaProducts = useCallback((client: LegacyDashboardClient) => {
    setSgaModalClient(client);
    setSgaModalOpen(true);
  }, []);

  const hasActiveFilters = searchTerm.length > 0 || countActiveLegacyDashboardFilters(filters) > 0;

  if (error) {
    return <ErrorState error={error instanceof Error ? error.message : String(error)} onRetry={() => refetch()} />;
  }

  if (isInitialLoad) {
    return <LoadingState />;
  }

  return (
    <div className="px-6 pt-4 pb-10 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('pageDescription')}</p>
      </div>

      <SummaryCards {...summary} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <FiltersSection
            searchTerm={searchTerm}
            onSearch={handleSearch}
            filters={filters}
            onFiltersChange={setFilters}
            clients={displayedClients}
            {...filterOptions}
          />
        </div>
        <TotalResults total={displayedClients.length} />
      </div>

      {displayedClients.length > 0 ? (
        <LegacyDashboardTable
          clients={displayedClients}
          isFetching={isFetching}
          sorting={sorting}
          onSortingChange={setSorting}
          onViewSgaProducts={handleViewSgaProducts}
        />
      ) : (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.SHOPS}
          title={t('empty.title')}
          description={t('empty.description')}
        />
      )}

      <SgaProductsModal client={sgaModalClient} open={sgaModalOpen} onOpenChange={setSgaModalOpen} />
    </div>
  );
};
